/**
 * A save made stale by another one (409 with the stored version in `data`):
 * one dialog for the whole app (`StaleSaveDialogHost`) asks between the stored
 * version (reload) and mine (saved over it on purpose). No React here, so the
 * SDK resources can use it too.
 */

export type StaleChoice = 'reload' | 'overwrite' | 'cancel'

interface StaleRequest {
  resolve: (choice: StaleChoice) => void
}

/* The dialog host (`StaleSaveDialogHost`) answers; without one mounted, a 409 is a reload. */
let host: ((request: StaleRequest) => void) | null = null

export const registerStaleSaveHost = (fn: ((request: StaleRequest) => void) | null) => {
  host = fn
}

export const askStaleSave = (): Promise<StaleChoice> => new Promise((resolve) => {
  if (!host) {
    resolve('reload')
    return
  }
  host({ resolve })
})

/** Thrown by `saveWithVersion` after the person chose to reload or to keep editing: nothing to report. */
export class StaleSaveHandled extends Error {
  constructor(public choice: StaleChoice) {
    super('stale save handled')
    this.name = 'StaleSaveHandled'
  }
}

export const isStaleHandled = (err: unknown): err is StaleSaveHandled => err instanceof StaleSaveHandled

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status

/** The current version a 409 brought (`data[field]`), or null. */
export const currentVersionOf = (err: unknown, field = 'updatedAt'): string | null => {
  const data = (err as { response?: { data?: { data?: Record<string, unknown> } } })?.response?.data?.data
  const value = data && data[field]
  return typeof value === 'string' ? value : null
}

export const isStaleConflict = (err: unknown, field = 'updatedAt') => {
  if (statusOf(err) !== 409) return false
  const errors = (err as { response?: { data?: { errors?: { param?: string }[] } } })?.response?.data?.errors
  /* The editors' 409 names the version field; another 409 (already published, a slug taken) is not this. */
  return !Array.isArray(errors) || !errors.length || errors.some((e) => e?.param === field)
}

/**
 * Runs a save with the version read. On a stale 409 asks the person: overwrite
 * sends it again with the stored version; reload calls `onReload` and throws
 * `StaleSaveHandled`; cancel (keep editing) throws it too.
 */
export const saveWithVersion = async <T,>(
  save: (version: string | null | undefined) => Promise<T>,
  { version, field = 'updatedAt', onReload }: { version: string | null | undefined; field?: string; onReload: () => unknown },
): Promise<T> => {
  try {
    return await save(version)
  } catch (err) {
    if (!isStaleConflict(err, field)) throw err
    const choice = await askStaleSave()
    if (choice === 'overwrite') return save(currentVersionOf(err, field) ?? undefined)
    if (choice === 'reload') await onReload()
    throw new StaleSaveHandled(choice)
  }
}
